import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { BookOpen, Clock, ArrowRight, User } from 'lucide-react';
import { siteContent } from '../data/mockData';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';

const BlogPreview = ({ onNavigate }) => {
  const content = siteContent.en;
  const [headerRef, headerVisible] = useScrollAnimation();
  const [articlesRef, visibleArticles] = useStaggeredAnimation(3, 200);
  const [ctaRef, ctaVisible] = useScrollAnimation(300);

  const handleReadMore = (articleId) => {
    if (onNavigate) {
      onNavigate('article', articleId);
    }
  };

  const handleViewAllInsights = () => {
    if (onNavigate) {
      onNavigate('insights');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <section className="py-20 lg:py-32 bg-gradient-to-b from-white to-[#f0f0e8]/20">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div ref={headerRef} className={`text-center mb-16 animate-fade-in-up ${headerVisible ? 'visible' : ''}`}>
          <div className="inline-flex items-center bg-[#f0f0e8] rounded-full px-4 py-2 mb-6">
            <BookOpen className="w-4 h-4 text-[#c41927] mr-2" />
            <span className="text-sm font-semibold text-[#011431] uppercase tracking-wide">Latest Insights</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-[#011431] mb-6">
            Industry Expertise & Best Practices
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay informed with the latest regulatory updates, quality insights, and proven strategies from our expert team.
          </p>
        </div>

        {/* Featured Articles Preview - Only show 3 articles */}
        <div ref={articlesRef} className="grid md:grid-cols-3 gap-8 mb-12">
          {content.blogPosts?.slice(0, 3).map((post, index) => (
            <Card key={post.id} className={`group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-1 animate-scale-up ${visibleArticles.includes(index) ? 'visible' : ''}`}>
              {/* Article Image */}
              <div className="h-48 overflow-hidden relative">
                <img 
                  src={post.image} 
                  alt={post.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="secondary" className="bg-[#f0f0e8] text-[#011431] text-xs">
                    {post.category}
                  </Badge>
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="w-3 h-3 mr-1" />
                    {post.readTime}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#011431] line-clamp-2 group-hover:text-[#c41927] transition-colors">
                  {post.title}
                </h3>
              </CardHeader>
              
              <CardContent className="pt-0">
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center text-xs text-gray-500">
                    <User className="w-3 h-3 mr-1" />
                    {post.author}
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatDate(post.date)}
                  </span>
                </div>
                
                <Button 
                  variant="ghost" 
                  onClick={() => handleReadMore(post.id)}
                  className="text-[#c41927] hover:bg-[#c41927] hover:text-white w-full font-semibold text-sm group-hover:translate-x-1 transition-all duration-300"
                >
                  Read Article
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* View All Insights CTA */}
        <div ref={ctaRef} className={`text-center animate-fade-in-up ${ctaVisible ? 'visible' : ''}`}>
          <Button 
            onClick={handleViewAllInsights}
            className="bg-[#c41927] hover:bg-[#a01520] text-white font-semibold px-8 py-3 text-lg group transition-all duration-300 transform hover:scale-105"
          >
            View All Insights
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;